import { Modal, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Draft } from 'immer';
import React, { CSSProperties, useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { ID } from '../../../global'; // e.g. type ID = string | number;
import { PageParam } from '@/models/common';
import { trueFilter } from '@/utils/common';
import { DataSource } from './datasource';
import styles from './style.less';

type BaseSelectorProps<T> = {
  selectStyle?: CSSProperties;
  selectClassName?: string;
  dataFilter?: (i: T) => boolean;

  // modal 独有
  open?: boolean;
  onCancel?: () => void;
  title?: React.ReactNode;
};

type IDSingleProps<T> = {
  multiple?: false;
  useEntity?: false;
  value?: ID;
  onChange?: (val: ID) => void;
} & BaseSelectorProps<T>;

type IDMultipleProps<T> = {
  multiple: true;
  useEntity?: false;
  value?: ID[];
  onChange?: (val: ID[]) => void;
} & BaseSelectorProps<T>;

type EntitySingleProps<T> = {
  multiple?: false;
  useEntity: true;
  value?: T;
  onChange?: (val: T | undefined) => void;
} & BaseSelectorProps<T>;

type EntityMultipleProps<T> = {
  multiple: true;
  useEntity: true;
  value?: T[];
  onChange?: (val: T[]) => void;
} & BaseSelectorProps<T>;

export type SelectorProps<T> =
  | IDSingleProps<T>
  | IDMultipleProps<T>
  | EntitySingleProps<T>
  | EntityMultipleProps<T>;

// type FilterValue = {};

// ------------------ 主组件工厂 ------------------
export const createModalSelector = <T extends object, FilterValue extends object = {}>({
  datasource,
  tableColumns,
  title: factoryTitle = '选择器',
  Filter,
}: {
  datasource: DataSource<T>;
  tableColumns: ColumnsType<T>;
  title?: React.ReactNode;
  Filter?: React.ComponentType<{ onFilter: (filters: FilterValue) => void }>;
}) => {
  return function SelectorComponent(props: SelectorProps<T>) {
    const {
      value,
      onChange,
      onCancel,
      selectStyle,
      selectClassName,
      dataFilter = trueFilter,
      open,
      title,
    } = props;
    // const [visible, setVisible] = useSyncedState(propVisible);

    const multiple = props.multiple ?? false;
    const useEntity = props.useEntity ?? false;
    const toID = (v: ID | T): ID => (typeof v === 'object' ? datasource.getValue(v) : (v as ID));

    const [selected, updateSelected] = useImmer<ID[]>(
      multiple
        ? Array.isArray(value)
          ? (value as (ID | T)[]).map(toID)
          : []
        : value !== undefined
        ? [toID(value as ID | T)]
        : []
    );

    const [dataList, setDataList] = useState<T[]>();
    // const [valueMap, setValueMap] = useState<Map<ID, T>>(new Map());
    const [valueMap, setValueMap] = useImmer<Map<ID, T>>(new Map());
    const [loading, setLoading] = useState(false);
    const [queryInfo, updateQueryInfo] = useImmer<{ pageParam: PageParam; filter: FilterValue }>({
      pageParam: { current: 1, pageSize: 10 },
      filter: {} as FilterValue,
    });

    const loadData = () => {
      setLoading(true);
      datasource
        .getList({
          pageParam: queryInfo.pageParam,
          filter: queryInfo.filter,
        })
        .then(list => {
          const filtered = list.filter(dataFilter);
          setDataList(filtered);
          setValueMap(draft => {
            for (const item of filtered) {
              draft.set(datasource.getValue(item), item as Draft<T>);
            }
          });
        })
        .finally(() => setLoading(false));
    };

    useEffect(() => {
      if (open) {
        // loadData({ current: 1, pageSize: 1 });
        loadData();
      }
    }, [open, queryInfo]);

    const removeItem = (id: ID) => {
      updateSelected(draft => {
        const index = draft.indexOf(id);
        if (index !== -1) {
          draft.splice(index, 1);
        }
      });
    };

    const handleChange = (selected: ID[]) => {
      updateSelected(() => selected);
    };

    const onConfirm = () => {
      if (!onChange) return;

      if (selected.length === 0) {
        (onChange as any)(multiple ? [] : undefined);
        return;
      }

      if (multiple) {
        const ids = selected as ID[];
        const result = useEntity ? ids.map(id => valueMap.get(id) as T) : ids;
        (onChange as any)(result);
      } else {
        const id = selected[0];
        const result = useEntity ? valueMap.get(id) : id;
        (onChange as any)(result);
      }
    };

    return (
      <>
        <Modal
          width={750}
          onCancel={onCancel}
          open={open}
          title={title || factoryTitle}
          onOk={onConfirm}>
          {Filter && (
            <Filter
              onFilter={filters => {
                updateQueryInfo(draft => {
                  draft.pageParam.current = 1;
                  draft.filter = filters as Draft<FilterValue>;
                });
              }}
            />
          )}
          <Table<T>
            bordered
            dataSource={dataList}
            columns={tableColumns}
            scroll={{ x: 'max-content' }}
            rowKey={v => datasource.getKey(v)}
            loading={loading}
            style={selectStyle}
            className={selectClassName}
            pagination={{
              ...datasource.getPageInfo(),
            }}
            onChange={pageInfo => {
              // loadData();
              updateQueryInfo(draft => {
                draft.pageParam.current = pageInfo.current;
                draft.pageParam.pageSize = pageInfo.pageSize;
              });
            }}
            rowSelection={{
              type: multiple ? 'checkbox' : 'radio',
              selectedRowKeys: selected,
              onChange: (selectedRowKeys: React.Key[]) => {
                handleChange(selectedRowKeys as ID[]);
              },
            }}
          />
          <div>
            <Typography.Title level={5}>已选择({selected.length}):</Typography.Title>
            <div className={styles.selected}>
              {selected.map(id => {
                const entity = valueMap.get(id);
                const fallback = id;
                return (
                  <Tag
                    closable
                    key={id}
                    onClose={() => {
                      removeItem(id);
                    }}>
                    {entity ? datasource.getLabel(entity) : fallback}
                  </Tag>
                );
              })}
            </div>
          </div>
        </Modal>
        {/* <Button
          onClick={() => {
            setVisible(true);
          }}>
          已选择
        </Button> */}
      </>
    );
  };
};
